/* AI writing credits: the purchasable top-up, and the one place either side reads its price from.
 *
 * ## This is not `PlanFeatures.aiCreditsPerBrand`
 *
 * The plan allowance is a monthly ration that resets on the 1st — `BrandAiUsage`, read and charged
 * by `backend/src/modules/ai/service.ts`. This is the opposite: a balance bought with real money
 * that never resets and never expires, drawn from only once the monthly ration is exhausted —
 * `BrandAiCredits`, the same shape `shared/src/mediaCredits.ts` already uses for image/video
 * credits, applied here to the writing assistant's own ledger.
 *
 * ## The price
 *
 * One credit is a flat **0.10** of whatever currency the workspace is billed in — not a converted
 * equivalent value, the same number in every currency this product offers, which is what makes
 * `AI_CREDIT_PRICE_MINOR_UNITS` a single constant rather than a table. A workspace's currency is
 * `Organization.currency`, set once in Settings; nothing here chooses it.
 *
 * ## Why a free quantity, not packs (contrast with `mediaCredits.ts`)
 *
 * Media credits are sold in fixed packs because the margin funds a real provider cost per
 * generation. A writing credit costs the operator a fraction of a cent regardless of how many are
 * bought, so there is no economic reason to steer toward a bigger pack — the price is linear, and
 * the only thing the server validates is that the requested quantity is sane (`MIN`/`MAX` below),
 * never that it matches a named pack.
 */

/** The three currencies a workspace may bill in. Lower-case, Stripe's own convention. */
export const AI_CREDIT_CURRENCIES = ['eur', 'gbp', 'usd'] as const
export type AiCreditCurrency = (typeof AI_CREDIT_CURRENCIES)[number]

export const isAiCreditCurrency = (value: string): value is AiCreditCurrency =>
  (AI_CREDIT_CURRENCIES as readonly string[]).includes(value)

/** In the smallest unit of whichever currency — 10 cents, 10 pence, 10 cents. Same number, three meanings. */
export const AI_CREDIT_PRICE_MINOR_UNITS = 10

/** Below this, a charge risks falling under Stripe's own per-currency minimum. */
export const AI_CREDIT_PURCHASE_MIN = 10
/** A sane ceiling against a fat-fingered quantity — 500 of whichever currency's major unit. */
export const AI_CREDIT_PURCHASE_MAX = 5_000

export const priceForAiCredits = (credits: number): number => credits * AI_CREDIT_PRICE_MINOR_UNITS

/**
 * What `GET /brands/:brandId/ai/credits` answers: the same `CreditView` every other AI endpoint
 * returns (`used`/`limit`/`remaining`/`purchased`/`resetsAt` — see `backend/src/modules/ai/service.ts`),
 * plus the facts the buy dialog needs that no other endpoint carries.
 */
export interface AiCreditBalance {
  /** This month's plan usage so far. */
  used: number
  /** `PlanFeatures.aiCreditsPerBrand` for this workspace's plan. */
  limit: number
  /** What `limit - used` (floored at zero) plus the purchased balance below add up to — the one
      number every gate across the app checks. */
  remaining: number
  /** The purchased, non-expiring slice of `remaining` — on top of whatever the plan still has this month. */
  purchased: number
  /** First instant of next month, UTC — when `used` resets. Purchased credits never expire. */
  resetsAt: string
  /** Everything this brand has ever been credited by a paid purchase. Refunds are not purchases and never land here. */
  lifetimePurchased: number
  pricePerCreditMinorUnits: number
  currency: AiCreditCurrency
  minPurchase: number
  maxPurchase: number
}

/** One paid purchase of writing credits, from `BrandAiCreditPurchase` — `AiCreditBalance`'s own
    `lifetimePurchased` is a running total; this is the row-by-row history behind it. */
export interface AiCreditPurchase {
  /** Stripe's own id for the paid session — stable, and the row's one unique fact; use it as the
      React key rather than inventing a composite one. */
  stripeCheckoutSessionId: string
  credits: number
  amountCents: number
  currency: AiCreditCurrency
  /** When the checkout session was created, ISO. */
  purchasedAt: string
}

/** One calendar month's plan usage, from `BrandAiUsage`. */
export interface AiMonthlyUsage {
  /** `YYYY-MM`, UTC. */
  period: string
  used: number
}

/** The eight things that can spend a writing credit, named for what they are in the product —
    not for the module that happens to implement them. */
export const AI_CREDIT_USAGE_SERVICES = [
  'ASSIST',
  'SCHEDULING_BATCH',
  'CHAT',
  'INBOX_REPLY',
  'PERFORMANCE_INSIGHT',
  'FLOW_PROPOSE',
  'DRAFT_AUTO',
  'DRAFT_REGENERATE',
] as const
export type AiCreditUsageService = (typeof AI_CREDIT_USAGE_SERVICES)[number]

/** One spend, from `AiCreditUsageEvent` — who, on what, how many. `user` is null only for
    `DRAFT_AUTO`, the feed/competitor polling pass that runs on a schedule with no acting person. */
export interface AiCreditUsageEvent {
  id: string
  user: { id: string; name: string; email: string } | null
  service: AiCreditUsageService
  credits: number
  createdAt: string
}

/** What `GET /brands/:brandId/ai/credits/history` answers — the dedicated credits page's whole
    story: every purchase ever made, a year of monthly usage, and who spent what on which feature.
    `canViewAll` is false for anyone who is not an `OWNER`/`ADMIN`: `purchases` is then always
    empty and `usageEvents` holds only that viewer's own rows — the server's decision, not a hint
    for the UI to re-derive from the viewer's own role. */
export interface AiCreditHistory {
  /** Newest first. */
  purchases: AiCreditPurchase[]
  /** Newest period first. */
  usageByMonth: AiMonthlyUsage[]
  /** Newest first, capped. */
  usageEvents: AiCreditUsageEvent[]
  canViewAll: boolean
}
